import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Route,
} from "react-router-dom";
import { Center, Spinner, VStack, Text, Button } from "@chakra-ui/react";
import { RootLayout } from "../components/layouts/RootLayout";
import { routes as browseRoutes } from "./browse/routes";
import { routes as downloadsPaths } from "./downloads/routes";
import { routes as inboxRoutes } from "./inbox/routes";
import { routes as newsPaths } from "./news/routes";
import { routes as shelfPaths } from "./shelf/routes";
// import { routes as adminPaths } from "./admin/routes";
import { routes as readerPaths } from "./reader/routes";
import { routes as suggestionFeedRoutes } from "./suggestionFeed/routes";
import { routes as goldPaths } from "./gold/routes";
import { routes as monthlyComicsPaths } from "./monthlyComics/routes";
import { routes as giftPaths } from "./gift/routes";
import RootScreen from "./root/RootScreen";
import { routes as legalPaths } from "./legal/routes";
import { routes as newsRoutes } from "./news/routes";
import { routes as globalChatRoutes } from "./globalChat/routes";
import { routes as redemptionSuccessRoutes } from "./redemptionSuccess/routes";
import { routes as valkyraeRoutes } from "./valkyrae/routes";
import { routes as trendsRoutes } from "./trends/routes";
import { routes as expandedContentRoutes } from "./expandedContent/routes";
import { routes as holidayBundleRoutes } from "./holidayBundle/routes";
import { routes as ultimateXmenBundleRoutes } from "./ultimateXmenBundle/routes";
import { routes as settingsRoutes } from "./settings/routes";
import { routes as aboutRoutes } from "./about/routes";

// Lazy-loaded admin panel — keeps admin code out of the public bundle
const AdminNextApp = lazy(() => import("./adminNext/AdminNextApp"));
import { CMSPageResolver } from "./cms/CMSPageResolver";

// Suspense fallback for the lazy-loaded admin bundle. Uses position: fixed
// instead of flex sizing so the spinner is centered in the viewport regardless
// of whatever flex ancestor chain the router has placed around <Outlet/> — the
// previous flex={1} w="100%" was collapsing to a narrow column during the
// initial boot sequence and the spinner ended up in the lower-left corner.
function AdminLoadingFallback() {
  return (
    <Center position="fixed" inset={0} bg="#0E0F11" zIndex={10}>
      <Spinner size="xl" color="orange.400" />
    </Center>
  );
}

function AdminChunkError() {
  return (
    <Center position="fixed" inset={0} bg="#0E0F11" zIndex={10}>
      <VStack spacing={4}>
        <Text color="red.400" fontSize="xl">Failed to load admin panel</Text>
        <Button colorScheme="orange" size="sm" onClick={() => window.location.reload()}>Retry</Button>
      </VStack>
    </Center>
  );
}

export const applicationRoutes = createRoutesFromElements(
  <Route path="/" element={<RootLayout />}>
    {browseRoutes}
    {downloadsPaths}
    {shelfPaths}
    <Route path="library" element={<Navigate to="/my-shelf/library" replace />} />
    {newsPaths}
    {inboxRoutes}
    {readerPaths}
    {suggestionFeedRoutes}
    {goldPaths}
    {monthlyComicsPaths}
    {giftPaths}
    {legalPaths}
    {newsRoutes}
    {globalChatRoutes}
    {redemptionSuccessRoutes}
    {valkyraeRoutes}
    {trendsRoutes}
    {expandedContentRoutes}
    <Route
      path="admin-next/*"
      element={
        <Suspense fallback={<AdminLoadingFallback />}>
          <AdminNextApp />
        </Suspense>
      }
      errorElement={<AdminChunkError />}
    />
    {aboutRoutes}
    {holidayBundleRoutes}
    {ultimateXmenBundleRoutes}
    {settingsRoutes}
    {/*{window.gc.global.is_superadmin && adminPaths}*/}
    <Route path="/deals" Component={RootScreen} />
    <Route path="/new" Component={RootScreen} />
    <Route index Component={RootScreen} />
    <Route path="channel/:channel?/:slug?" Component={RootScreen} />
    <Route path="*" Component={CMSPageResolver} />
  </Route>,
);

export const router = createBrowserRouter(applicationRoutes);
